
import { LoadMoreButton } from './LoadMoreButton';
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { PostCard } from '../PostCard';
import { CastCard } from '../CastCard';
import { AdPlacement } from '../AdPlacement';
import { IsoCastAdPlacement } from '../IsoCastAdPlacement';
import { AnnouncementCard } from '../AnnouncementCard';
import { GrantAnnouncement } from '../grants/GrantAnnouncement';
import { AuthModal } from '../AuthModal';
import { LazyFeedRenderer } from '../LazyFeedRenderer';
import { MyTeamsFarmsList } from './MyTeamsFarmsList';
import { injectAdsIntoFeed, isAdItem, isIsoCastAd, isExternalAd, isAnnouncementItem } from '@/utils/feedWithAds';
import type { Post } from '@/hooks/usePosts';
import { useState } from 'react';

interface FeedContentProps {
  loading: boolean;
  error: string | null;
  filteredFeedItems: any[];
  allFeedItems: any[];
  postTypeFilter: string;
  posts: any[];
  casts: any[];
  announcements?: any[];
  locationGrants?: any[];
  onLike: (id: string, type: 'post' | 'cast') => void;
  onComment: (id: string) => void;
  onRefresh: () => void;
  onSignInRequired?: () => void;
  // Pagination props
  postsHasMore?: boolean;
  castsHasMore?: boolean;
  onLoadMorePosts?: () => void;
  onLoadMoreCasts?: () => void;
  postsLoading?: boolean;
  castsLoading?: boolean;
  isLiteMode?: boolean;
  // User location for grants
  userLocation?: string;
  userState?: string;
}

export const FeedContent = ({
  loading,
  error,
  filteredFeedItems,
  allFeedItems,
  postTypeFilter,
  posts,
  casts,
  announcements = [],
  locationGrants = [],
  onLike,
  onComment,
  onRefresh,
  onSignInRequired,
  // Pagination props
  postsHasMore = false,
  castsHasMore = false,
  onLoadMorePosts,
  onLoadMoreCasts,
  postsLoading = false,
  castsLoading = false,
  isLiteMode = false,
  // User location for grants
  userLocation,
  userState
}: FeedContentProps) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  
  if (postTypeFilter === 'my_teams') {
    return (
      <div className="p-3">
        <MyTeamsFarmsList />
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="p-3">
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="rounded-xl shadow-sm border border-white/20 overflow-hidden p-4 backdrop-blur-md bg-white/70">
              <div className="flex items-start space-x-3 mb-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-5 w-32" />
                  </div>
                  <Skeleton className="h-4 w-48 mt-1" />
                </div>
              </div>
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mb-2" />
              <Skeleton className="h-4 w-2/3 mb-4" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <div className="text-red-500 p-4 bg-red-50 rounded-lg border border-red-200">
          <h3 className="font-medium mb-2">Error loading feed</h3>
          <p className="text-sm">{error}</p>
          <Button 
            onClick={onRefresh} 
            variant="outline" 
            size="sm" 
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (filteredFeedItems.length === 0) {
    return (
      <div className="p-3">
        <div className="text-white text-center py-2">
          {allFeedItems.length === 0 ? (
            <div className="space-y-4">
              <p>No posts or casts available.</p>
              <div className="text-sm text-white/80">
                Posts: {posts.length} | Casts: {casts.length}
              </div>
              <Button 
                onClick={onRefresh} 
                variant="outline"
              >
                Refresh Feed
              </Button>
            </div>
          ) : postTypeFilter === 'casts' ? (
            "No ISO cast posts match your current filters. Try adjusting your filter criteria."
          ) : postTypeFilter === 'my_teams' ? (
            "No team farms found. Join teams or invite farmers to see their farms here."
          ) : (
            ""
          )}
        </div>
      </div>
    );
  }

  // Only inject ads and announcements when showing all items or videos (not for casts-only view)
  const shouldInjectAds = postTypeFilter !== 'casts';
  const feedWithAds = shouldInjectAds 
    ? injectAdsIntoFeed(filteredFeedItems, casts, announcements, 10, 3)
    : filteredFeedItems;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="p-3">
        <div className="space-y-6">
          <LazyFeedRenderer
            items={feedWithAds}
            posts={posts}
            onLike={onLike}
            onComment={onComment}
          />
          
          {/* Load More Buttons */}
          {postTypeFilter === 'videos' && onLoadMorePosts && (
            <LoadMoreButton
              loading={postsLoading}
              hasMore={postsHasMore}
              onLoadMore={onLoadMorePosts}
              totalShown={posts.length}
              isLiteMode={isLiteMode}
            />
          )}
          
          {postTypeFilter === 'casts' && onLoadMoreCasts && (
            <LoadMoreButton
              loading={castsLoading}
              hasMore={castsHasMore}
              onLoadMore={onLoadMoreCasts}
              totalShown={casts.length}
              isLiteMode={isLiteMode}
            />
          )}
          
          {postTypeFilter === 'all' && onLoadMorePosts && onLoadMoreCasts && (
            <div className="space-y-4">
              {(postsHasMore || castsHasMore) ? (
                <div className="flex gap-4 justify-center">
                  {postsHasMore && (
                    <LoadMoreButton
                      loading={postsLoading}
                      hasMore={postsHasMore}
                      onLoadMore={onLoadMorePosts}
                      totalShown={posts.length}
                      isLiteMode={isLiteMode}
                    />
                  )}
                  {castsHasMore && (
                    <LoadMoreButton
                      loading={castsLoading}
                      hasMore={castsHasMore}
                      onLoadMore={onLoadMoreCasts}
                      totalShown={casts.length}
                      isLiteMode={isLiteMode}
                    />
                  )}
                </div>
              ) : (
                <LoadMoreButton
                  loading={false}
                  hasMore={false}
                  onLoadMore={() => {}}
                  totalShown={filteredFeedItems.length}
                  isLiteMode={isLiteMode}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <AuthModal 
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        feature="grant details"
      />
    </div>
  );
};
